import { normalizeEmail } from '../services/guestAccountLinkingService';

/**
 * Guest with admin-assigned email awaiting registered-user merge.
 */
export function isPendingLinkGuest(player) {
  return (
    player?.is_guest === true &&
    Boolean(player?.guest_invited_email) &&
    player?.merged_at == null
  );
}

/**
 * Member player ids to hide when a pending-link guest exists for the same email.
 * @param {Array<{ id: string, is_guest?: boolean, email?: string, guest_invited_email?: string, merged_at?: string|null }>} players
 * @returns {Set<string>}
 */
export function findPendingLinkMemberIdsToHide(players) {
  const hideMemberIds = new Set();
  const guests = (players || []).filter(isPendingLinkGuest);
  const members = (players || []).filter((p) => !p?.is_guest && p?.email);

  const memberIdByEmail = new Map();
  members.forEach((member) => {
    const normalized = normalizeEmail(member.email);
    if (normalized) {
      memberIdByEmail.set(normalized, member.id);
    }
  });

  guests.forEach((guest) => {
    const normalized = normalizeEmail(guest.guest_invited_email);
    const memberId = memberIdByEmail.get(normalized);
    if (memberId) {
      hideMemberIds.add(memberId);
    }
  });

  return hideMemberIds;
}

/**
 * Drop registered-member duplicates when a pending-link guest exists.
 * Keeps members that are already in the session/signup context.
 *
 * @param {Array} players
 * @param {{ sessionPlayerStatus?: Record<string, string>, signedUpPlayerIds?: Set<string> }} [options]
 */
export function filterPlayersForHostSearch(players, options = {}) {
  const { sessionPlayerStatus = {}, signedUpPlayerIds = new Set() } = options;
  const hideMemberIds = findPendingLinkMemberIdsToHide(players);

  return (players || [])
    .filter((player) => {
      if (!hideMemberIds.has(player.id)) return true;
      if (sessionPlayerStatus[player.id] !== undefined) return true;
      if (signedUpPlayerIds.has(player.id)) return true;
      return false;
    })
    .map((player) => ({
      ...player,
      linkPending: isPendingLinkGuest(player),
    }));
}

/**
 * Badge metadata for roster/search player rows.
 */
export function getPlayerRosterTypeMeta(player) {
  if (!player?.is_guest) {
    return { label: 'Member', variant: 'primary' };
  }
  if (isPendingLinkGuest(player)) {
    return { label: 'Guest · link pending', variant: 'pending' };
  }
  return { label: 'Guest', variant: 'warning' };
}
